<!--
  LwSectionTabs: the row of page links under a section's heading (spec 49
  section 4.2).

  NOT LwTabs. LwTabs is a `role="tablist"` of buttons that switch panels in
  place, which is right for the views of one page (Billing's Unbilled /
  Billed / Batch) and wrong for navigation between pages: a screen reader
  told "tab, 2 of 5" expects the next panel, not a new URL and a new
  document title. These are links in a labelled <nav>, each its own route,
  so a copied URL opens on the right page and back/forward step between them.
  vue-router marks the current one; it is given `aria-current="page"` the
  way the rail's links are.

  It is drawn to read as the same family as LwTabs -- same type, same amber
  rule under the current one, same pill for a count, same horizontal scroll
  when the row is wider than the viewport -- because the two sit one above
  the other on pages that have both, and a person should not have to learn
  two tab looks to tell which row changes the page.
-->
<template>
  <nav class="lw-c-section-tabs" :aria-label="label">
    <div class="lw-c-section-tabs__scroller">
      <ul class="lw-c-section-tabs__list">
        <li v-for="tab in tabs" :key="tab.name" class="lw-c-section-tabs__item">
          <router-link
            v-slot="{ href, navigate, isActive }"
            :to="tab.to || { name: tab.name }"
            custom
          >
            <a
              class="lw-c-section-tabs__tab"
              :class="{ 'lw-c-section-tabs__tab--active': isActive }"
              :href="href"
              :aria-current="isActive ? 'page' : null"
              :data-section-tab="tab.name"
              @click="navigate"
            >
              <span class="lw-c-section-tabs__label">{{ tab.label }}</span>
              <span v-if="hasCount(tab)" class="lw-c-section-tabs__count">{{ tab.count }}</span>
            </a>
          </router-link>
        </li>
      </ul>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'LwSectionTabs',
  props: {
    // [{ name, label, count?, to? }]: `name` is the route name the tab opens
    // (and its key); `to` overrides the location when a tab needs query or
    // params as well.
    tabs: {
      type: Array,
      required: true,
      validator: value =>
        value.every(tab => tab && typeof tab.name === 'string' && typeof tab.label === 'string'),
    },
    // The landmark's accessible name ("Team sections"). Required: a page can
    // carry several <nav>s and an unlabelled one is not navigable.
    label: {
      type: String,
      required: true,
    },
  },
  methods: {
    hasCount(tab) {
      return tab.count !== null && tab.count !== undefined;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/tab-row';

// Billing's row exactly (sass/_tab-row.scss, shared with LwTabs). It was a
// white strip with a 20px inset of its own, which put a section's first tab
// 18px right of a page's and made Team and Billing look like two products.
.lw-c-section-tabs__scroller {
  @include lw-tab-row;
}

.lw-c-section-tabs__list {
  @include lw-tab-list;
  margin: 0;
  padding: 0;
  list-style: none;
}

.lw-c-section-tabs__tab {
  @include lw-tab;
}

.lw-c-section-tabs__tab--active {
  @include lw-tab-selected;
}

.lw-c-section-tabs__count {
  @include lw-tab-count;
}

.lw-c-section-tabs__tab--active .lw-c-section-tabs__count {
  @include lw-tab-count-selected;
}
</style>
